/**
 * 文件预览工具
 * 根据后端返回的 gameIconId / 附件 id 调用 /api/file/preview，获取 previewUrl
 *
 * 约定：
 * - 请求：POST /api/file/preview（通过 request.js 的 baseURL 拼出来）
 * - 入参：{ attachmentUuid: gameIconId }
 * - 响应示例：
 *   {
 *     "msg": "操作成功",
 *     "code": 200,
 *     "data": {
 *       "previewUrl": "http://...xxx.jpg",
 *       "fileKey": "...",
 *       "fileName": "..."
 *     }
 *   }
 */

import { post } from "./request";

const PREVIEW_API = "/file/preview";

// 简单内存缓存，避免重复请求相同附件
// 值结构：{ url: string, ts: number }，ts 为写入时间戳（ms）
const previewCache = new Map();
// 同一 UUID 下多文件预览 URL 列表缓存：{ urls: string[], ts: number }
const previewListCache = new Map();
// 缓存过期时间：24 小时
const PREVIEW_CACHE_TTL = 24 * 60 * 60 * 1000;

/**
 * 同步读取缓存中的预览链接（不请求接口），用于列表首屏立即展示已缓存的图片
 * @param {string|number} attachmentId
 * @returns {string} 有缓存且未过期时返回 url，否则返回 ""
 */
export function getCachedPreviewUrl(attachmentId) {
  if (attachmentId == null || attachmentId === "") return "";
  const cacheKey = String(attachmentId);
  const cached = previewCache.get(cacheKey);
  if (!cached || typeof cached.url !== "string") return "";
  const now = Date.now();
  if (cached.ts != null && now - cached.ts >= PREVIEW_CACHE_TTL) return "";
  return cached.url || "";
}

/**
 * 根据附件 id 获取预览链接
 * @param {string} attachmentId - 后端返回的附件 id（如 gameIconId）
 * @returns {Promise<string>} previewUrl（失败或无数据时返回空字符串）
 */
export async function getFilePreviewUrl(attachmentId) {
  if (attachmentId == null || attachmentId === "") return "";
  // 统一用字符串作为缓存 key，避免 123 与 "123" 重复请求
  const cacheKey = String(attachmentId);
  const now = Date.now();

  if (previewListCache.has(cacheKey)) {
    const entry = previewListCache.get(cacheKey);
    if (
      entry?.urls?.length &&
      (!entry.ts || now - entry.ts < PREVIEW_CACHE_TTL)
    ) {
      return entry.urls[0] || "";
    }
  }

  // 命中缓存且未过期时直接返回，不再请求接口
  if (previewCache.has(cacheKey)) {
    const cached = previewCache.get(cacheKey);
    if (cached && typeof cached.url === "string") {
      if (!cached.ts || now - cached.ts < PREVIEW_CACHE_TTL) {
        return cached.url || "";
      }
    }
  }

  try {
    const res = await post(PREVIEW_API, {
      attachmentUuid: attachmentId,
    });

    // 支持 data 为数组或单对象
    const list = Array.isArray(res?.data) ? res.data : [];
    const url =
      list[0]?.previewUrl ||
      (res?.data && typeof res.data === "object" && !Array.isArray(res.data)
        ? res.data.previewUrl
        : "") ||
      "";

    // 写入缓存并记录时间戳（key 与查询一致，保证再次进入页面能命中）
    previewCache.set(cacheKey, { url, ts: Date.now() });
    return url;
  } catch (e) {
    // 出错时静默失败，返回空字符串，避免影响主业务
    console.warn("获取文件预览链接失败:", e);
    return "";
  }
}

/**
 * 根据附件 UUID 获取该批次下全部文件的预览链接（如评论多图同一 attachmentUUID）
 * @param {string|number} attachmentId
 * @returns {Promise<string[]>}
 */
export async function getFilePreviewUrlList(attachmentId) {
  if (attachmentId == null || attachmentId === "") return [];
  const cacheKey = String(attachmentId);
  const now = Date.now();

  if (previewListCache.has(cacheKey)) {
    const entry = previewListCache.get(cacheKey);
    if (
      Array.isArray(entry?.urls) &&
      (!entry.ts || now - entry.ts < PREVIEW_CACHE_TTL)
    ) {
      return entry.urls.filter(Boolean);
    }
  }

  if (previewCache.has(cacheKey)) {
    const cached = previewCache.get(cacheKey);
    if (cached?.url && (!cached.ts || now - cached.ts < PREVIEW_CACHE_TTL)) {
      const urls = [cached.url];
      previewListCache.set(cacheKey, { urls, ts: cached.ts || now });
      return urls;
    }
  }

  try {
    const res = await post(PREVIEW_API, {
      attachmentUuid: attachmentId,
    });

    let urls = [];
    if (Array.isArray(res?.data)) {
      urls = res.data.map(item => item?.previewUrl).filter(Boolean);
    } else if (res?.data && typeof res.data === "object") {
      const u = res.data.previewUrl;
      if (u) urls = [u];
    }

    previewListCache.set(cacheKey, { urls, ts: Date.now() });
    if (urls.length > 0) {
      previewCache.set(cacheKey, { url: urls[0], ts: Date.now() });
    }
    return urls;
  } catch (e) {
    console.warn("获取文件预览链接列表失败:", e);
    return [];
  }
}

/**
 * 批量获取预览链接（与传入顺序一一对应）
 * @param {string[]} attachmentIds
 * @returns {Promise<string[]>}
 */
export async function getFilePreviewUrls(attachmentIds) {
  if (!Array.isArray(attachmentIds) || attachmentIds.length === 0) return [];
  const results = await Promise.all(
    attachmentIds.map(id => getFilePreviewUrl(id)),
  );
  return results;
}

export default {
  getFilePreviewUrl,
  getFilePreviewUrlList,
  getFilePreviewUrls,
  getCachedPreviewUrl,
};

